/**
 * Pure decision logic for which Properties Panel section (or the panel top) a change in selection
 * or section open/closed state should scroll to. Only covers this passive case — a direct user
 * action that opens a section (accordion header click, keyboard shortcut, nav-icon click, an
 * imperative "expand and open") always scrolls that section unconditionally and never calls this;
 * see `usePropertiesPanelScroll`.
 *
 * Priority, in order:
 *  0. The user just collapsed a section            -> the section right below it, open
 *                                                      or closed (it moves up into the
 *                                                      collapsed one's place; a pin
 *                                                      elsewhere doesn't win). The last
 *                                                      section keeps its own header in
 *                                                      place instead of jumping away.
 *  1. The selection's own section (Object/Type/      -> that section
 *     Photo) is open — `null` (nothing selected)
 *     never matches this, so it's simply skipped.
 *     Clicking a text box shows Type, a photo Photo,
 *     a shape Object — even with Layout/Effects open
 *  2. Layout/Effects is pinned (opened more          -> that section
 *     recently of the two) and still open
 *  3. The topmost currently-open section in fixed    -> that section
 *     panel order (Object, Type, Photo, Layout,
 *     Effects) — never opens a closed section just
 *     to make scrolling easier. Applies the same way
 *     whether or not anything is selected: closing a
 *     section a no-selection browsing session had
 *     open still lands on whatever else is open,
 *     instead of jumping back to the panel top.
 *  4. Nothing open (including "nothing selected      -> panel top
 *     and nothing manually opened either")
 */
import { PROPERTIES_PANEL_SECTION_ORDER } from '@/lib/panels/constants/properties-panel-section-order'
import type { PropertiesPanelScrollTarget } from './types/properties-panel-scroll-target'
import type { ResolvePropertiesPanelScrollTargetInput } from './types/resolve-properties-panel-scroll-target-input'

export function resolvePropertiesPanelScrollTarget({
  selectionDefaultSectionId,
  pinnedSectionId,
  isSectionOpen,
  collapsedSectionId = null,
}: ResolvePropertiesPanelScrollTargetInput): PropertiesPanelScrollTarget {
  if (collapsedSectionId) {
    const next =
      PROPERTIES_PANEL_SECTION_ORDER[PROPERTIES_PANEL_SECTION_ORDER.indexOf(collapsedSectionId) + 1]

    return next ?? collapsedSectionId
  }

  if (selectionDefaultSectionId && isSectionOpen(selectionDefaultSectionId)) {
    return selectionDefaultSectionId
  }

  if (pinnedSectionId && isSectionOpen(pinnedSectionId)) return pinnedSectionId

  return PROPERTIES_PANEL_SECTION_ORDER.find(isSectionOpen) ?? 'top'
}
